/* eslint-disable react-hooks/exhaustive-deps, no-useless-escape */
import { message } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import readXlsxFile from 'read-excel-file';
import { Row } from 'read-excel-file/types';

import { ProjectImport } from '../../../../model/entities';
import { bim360ProjectServicesTypes, CurrencyType } from '../../../../model/types';
import { useSetupProjectsImport } from './SetupProjectsImportContext';

enum AccColIndex {
    NAME = 0,
    NUMBER = 1,
    ACCOUNT = 2,
    TYPE = 3,
    TEMPLATE = 4,
    START_DATE = 5,
    END_DATE = 6,
    TIMEZONE = 7,
    VALUE = 8,
    CURRENCY = 9,
    ADDRESS_LINE_1 = 10,
    ADDRESS_LINE_2 = 11,
    CITY = 12,
    POSTAL_CODE = 13,
    COUNTRY = 14,
    REGION = 15,
}

enum Bim360ColIndex {
    NAME = 0,
    TYPE = 1,
    VALUE = 2,
    CURRENCY = 3,
    NUMBER = 4,
    START_DATE = 5,
    END_DATE = 6,
    LANGUAGE = 7,
    PROJECT_TEMPLATE = 8,
    TEMPLATE = 9,
    SERVICES = 10,
}

// TODO: the new regex should be created and date format should be confirmed

const inputValidationRegex = /[\w\s_\/-]+;[\w\s_\/-]+;[\w\s_\/-]+;[\w\s_\/-]+;[\w\s_\/-]+;[\w\s_\/-]+;[\w\s_\/-]+/gm;

const useUploadController = () => {
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedFileRows, setParsedFileRows] = useState<Row[]>([]);
    const [isInputError, setIsInputError] = useState(false);

    const [inputRows, setInputRows] = useState<Row[]>([]);

    const { setProjects, templates, selectedFile, setSelectedFile, inputValue, setInputValue, platform } =
        useSetupProjectsImport();

    const parseRowsCallback = (row: string[] | Row) => {
        return row as Row;
    };

    const parsedCsvIntoRows = (parsedCsv: string): Row[] => {
        const parsedResult = parsedCsv.split(/\r\n|\r|\n/).map((line) => line.split(','));
        return parsedResult.map(parseRowsCallback);
    };

    const parsedStringIntoRows = (value: string): Row[] => {
        const parsedResult = value.split(/\r\n|\r|\n/).map((line) => line.split(/;/));
        return parsedResult.map(parseRowsCallback);
    };

    const getServiceTypes = (rowIndex: number): string[] => {
        if (parsedFileRows[rowIndex].length <= Bim360ColIndex.SERVICES) {
            return ['doc_manager'];
        }
        const serviceTypes = parsedFileRows[rowIndex]
            .slice(Bim360ColIndex.SERVICES, parsedFileRows[rowIndex].length)
            .map((k) => bim360ProjectServicesTypes.find((t) => t.key === `${k}`)?.value || undefined)
            .filter((v) => !!v) as string[];

        const serviceTypesSet = new Set(serviceTypes);
        serviceTypesSet.add('doc_manager');

        return [...Array.from(serviceTypesSet)];
    };

    useEffect(() => {
        if (inputRows.length > 0) {
            const newProjects = inputRows.map((row, rowIndex): ProjectImport => {
                // name
                const nameCell = row[platform === 'BIM360' ? Bim360ColIndex.NAME : AccColIndex.NAME];
                const name = nameCell ? nameCell.toString() : '';

                // type
                const typeCell = row[platform === 'BIM360' ? Bim360ColIndex.TYPE : AccColIndex.TYPE];
                const type = typeCell ? typeCell.toString() : undefined;

                // job number
                const jobNumberCell = row[platform === 'BIM360' ? Bim360ColIndex.NUMBER : AccColIndex.NUMBER];
                const jobNumber = jobNumberCell ? jobNumberCell.toString() : undefined;

                // template
                const templateCell = row[platform === 'BIM360' ? Bim360ColIndex.TEMPLATE : AccColIndex.TEMPLATE];
                const template = templateCell ? templates.find((t) => t.name === templateCell.toString()) : undefined;

                // project template
                const projectTemplateCell = platform === 'ACC' ? undefined : row[Bim360ColIndex.PROJECT_TEMPLATE];
                const projectTemplate = projectTemplateCell;

                // start date
                const startDateCell = row[platform === 'BIM360' ? Bim360ColIndex.START_DATE : AccColIndex.START_DATE];
                const startDate = startDateCell ? moment(startDateCell.toString()) : undefined;

                // end date
                const endDateCell = row[platform === 'BIM360' ? Bim360ColIndex.END_DATE : AccColIndex.END_DATE];
                const endDate = endDateCell ? moment(endDateCell.toString()) : undefined;

                // language
                const languageCell = platform === 'ACC' ? undefined : row[Bim360ColIndex.LANGUAGE];
                const language = languageCell ? languageCell.toString() : undefined;

                // value
                const valueCell = platform === 'ACC' ? row[AccColIndex.VALUE] : row[Bim360ColIndex.VALUE];
                const value = valueCell ? Number(valueCell.toString()) : undefined;

                // currency
                const currencyCell = platform === 'ACC' ? row[AccColIndex.CURRENCY] : row[Bim360ColIndex.CURRENCY];
                const currency = currencyCell ? (currencyCell.toString() as CurrencyType) : undefined;

                // service types
                const serviceTypes = platform === 'ACC' ? undefined : getServiceTypes(rowIndex);

                // timezone
                const timezoneCell = platform === 'ACC' ? row[AccColIndex.TIMEZONE] : undefined;
                const timezone = timezoneCell ? timezoneCell.toString() : undefined;

                // address line 1
                const addressLine1Cell = platform === 'ACC' ? row[AccColIndex.ADDRESS_LINE_1] : undefined;
                const addressLine1 = addressLine1Cell ? addressLine1Cell.toString() : undefined;

                // address line 2
                const addressLine2Cell = platform === 'ACC' ? row[AccColIndex.ADDRESS_LINE_2] : undefined;
                const addressLine2 = addressLine2Cell ? addressLine2Cell.toString() : undefined;

                // city
                const cityCell = platform === 'ACC' ? row[AccColIndex.CITY] : undefined;
                const city = cityCell ? cityCell.toString() : undefined;

                // postal code
                const postalCodeCell = platform === 'ACC' ? row[AccColIndex.POSTAL_CODE] : undefined;
                const postalCode = postalCodeCell ? postalCodeCell.toString() : undefined;

                // country
                const countryCell = platform === 'ACC' ? row[AccColIndex.COUNTRY] : undefined;
                const country = countryCell ? countryCell.toString() : undefined;

                // region
                const regionCell = platform === 'ACC' ? row[AccColIndex.REGION] : undefined;
                const region = regionCell ? regionCell.toString() : undefined;

                return {
                    id: '',
                    name,
                    type,
                    jobNumber,
                    template,
                    projectTemplate,
                    platform,
                    startDate,
                    endDate,
                    language,
                    value,
                    currency,
                    serviceTypes,
                    timezone,
                    addressLine1,
                    addressLine2,
                    city,
                    postalCode,
                    country,
                    region,
                };
            });
            setProjects(newProjects);
        }
    }, [inputRows]);

    // handle uploaded file
    useEffect(() => {
        let fileReader: FileReader;
        let isCancel: boolean = false;

        if (selectedFile && selectedFile.type === 'text/csv') {
            fileReader = new FileReader();
            fileReader.onload = (e) => {
                const result = e.target?.result;
                if (typeof result === 'string' && !isCancel) {
                    setParsedFileRows(parsedCsvIntoRows(result).slice(1));
                }
            };
            fileReader.readAsText(selectedFile);
        } else if (
            selectedFile &&
            selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
            readXlsxFile(selectedFile).then((rows) => {
                const importedFile = rows.slice(1);
                setParsedFileRows(importedFile.map(parseRowsCallback));
            });
        }

        return () => {
            isCancel = true;
            if (fileReader && fileReader.readyState === 1) {
                fileReader.abort();
            }
        };
    }, [selectedFile]);

    const handleDrag = function (e: any) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleFile = function (file?: File) {
        if (
            file?.type !== 'text/csv' &&
            file?.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
            message.error(intl.formatMessage({ id: 'status.file.format' }));
            return;
        } else if (!file) {
            return;
        }
        setSelectedFile(file);
    };

    const handleDrop = function (e: any) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleChange = function (e: any) {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleInputValue = (e: any) => {
        setInputValue(e.target.value);
        if (!inputValidationRegex.test(e.target.value)) {
            setIsInputError(true);
        } else {
            setIsInputError(false);
        }
    };

    const handleSaveInputValue = useCallback(() => {
        if (selectedFile) {
            return inputRows;
        }

        setInputRows(parsedStringIntoRows(inputValue));
        return parsedStringIntoRows(inputValue);
    }, [inputValue, templates]);

    const handleUpload = useCallback(async () => {
        const savedValue = handleSaveInputValue();
        if (!parsedFileRows && inputValue) {
            setInputRows(savedValue);
        } else if (parsedFileRows.length) {
            setInputRows(parsedFileRows);
        }
    }, [parsedFileRows, inputValue]);

    return {
        inputRef,
        handleDrag,
        handleDrop,
        handleChange,
        dragActive,
        selectedFile,
        handleInputValue,
        handleSaveInputValue,
        inputValue,
        handleUpload,
        isInputError,
    };
};

export default useUploadController;
